import React, { useState } from "react";
import Button from "../../../components/BaseUI/Button";
import IconButton from "../../../components/BaseUI/Button/IconButton";
import Input from "../../../components/BaseUI/Input";
import {
  Dropdown,
  DropdownContent,
  DropdownItem,
} from "../../../components/BaseUI/Dropdown";
import Label from "../../../components/BaseUI/Label";
import Select from "../../../components/BaseUI/Select";
import { FiPlus, FiTrash2, FiChevronUp, FiChevronDown } from "react-icons/fi";

const productLineTypeOptions = [
  { value: "production", label: "Üretim" },
  { value: "product", label: "Ürün" },
];
const dataEntryTypeOptions = [
  { value: "manual", label: "Manuel" },
  { value: "from_machine", label: "Makineden" },
];

function ProductionLineForm({
  formData,
  setFormData,
  machines,
  inventoryItems,
  editingLine,
  addToast,
  onSubmit,
  onReset,
}) {
  const [addingItemForMachine, setAddingItemForMachine] = useState({
    machineIdx: null,
    inventoryItemId: "",
    quantityUsed: 1,
  });
  const [openInventories, setOpenInventories] = useState({});

  const safeMachines = Array.isArray(machines) ? machines : [];
  const safeInventoryItems = Array.isArray(inventoryItems)
    ? inventoryItems
    : [];
  const safeMachineItems = Array.isArray(formData?.machineItems)
    ? formData.machineItems
    : [];

  const getMachineName = (id) =>
    safeMachines.find((m) => m && m._id === id)?.name || id;

  const getInvName = (id) => {
    const item = safeInventoryItems.find((i) => i && i._id === id);
    return item ? `${item.name || ""} (${item.code || ""})` : id;
  };

  const derivedEntryId = (idx) => {
    if (idx <= 0) return "";
    return safeMachineItems[idx - 1]?.outputItemId || "";
  };

  const addMachine = () => {
    const available = safeMachines.filter(
      (m) =>
        m &&
        !safeMachineItems.some(
          (mi) => (mi.machineId?._id || mi.machineId) === m._id,
        ),
    );
    if (available.length === 0) {
      addToast("Tüm makineler zaten ekli", "warning");
      return;
    }
    setFormData({
      ...formData,
      machineItems: [
        ...safeMachineItems,
        {
          machineId: available[0]._id,
          lineItems: [],
          entryItemId: undefined,
          outputItemId: undefined,
          inputAmount: 1,
          outputAmount: 1,
          inputPerOutput: 1,
        },
      ],
    });
  };

  const removeMachine = (idx) => {
    setFormData({
      ...formData,
      machineItems: safeMachineItems.filter((_, i) => i !== idx),
    });
    if (addingItemForMachine.machineIdx === idx)
      setAddingItemForMachine({
        machineIdx: null,
        inventoryItemId: "",
        quantityUsed: 1,
      });
    else if (addingItemForMachine.machineIdx > idx)
      setAddingItemForMachine((p) => ({ ...p, machineIdx: p.machineIdx - 1 }));
  };

  const moveMachine = (idx, dir) => {
    const arr = [...safeMachineItems];
    const next = idx + dir;
    if (next < 0 || next >= arr.length) return;
    [arr[idx], arr[next]] = [arr[next], arr[idx]];
    setFormData({ ...formData, machineItems: arr });
    if (addingItemForMachine.machineIdx === idx)
      setAddingItemForMachine((p) => ({ ...p, machineIdx: next }));
    else if (addingItemForMachine.machineIdx === next)
      setAddingItemForMachine((p) => ({ ...p, machineIdx: idx }));
  };

  const addLineItemForMachine = (machineIdx) => {
    const { inventoryItemId, quantityUsed } = addingItemForMachine;
    if (!inventoryItemId || quantityUsed <= 0) {
      addToast("Geçerli envanter ve miktar seçin", "warning");
      return;
    }
    const mi = safeMachineItems[machineIdx];
    const exists = (mi.lineItems || []).some(
      (li) =>
        (li.inventoryItemId?._id || li.inventoryItemId) === inventoryItemId,
    );
    if (exists) {
      addToast("Bu kalem zaten ekli", "warning");
      return;
    }
    const updated = [...safeMachineItems];
    updated[machineIdx] = {
      ...updated[machineIdx],
      lineItems: [
        ...(updated[machineIdx].lineItems || []),
        { inventoryItemId, quantityUsed },
      ],
    };
    setFormData({ ...formData, machineItems: updated });
    setAddingItemForMachine({
      machineIdx,
      inventoryItemId: "",
      quantityUsed: 1,
    });
  };

  const removeLineItem = (machineIdx, itemIdx) => {
    const updated = [...safeMachineItems];
    updated[machineIdx] = {
      ...updated[machineIdx],
      lineItems: (updated[machineIdx].lineItems || []).filter(
        (_, i) => i !== itemIdx,
      ),
    };
    setFormData({ ...formData, machineItems: updated });
  };

  return (
    <div className="production-line-form">
      <div className="form-row">
        <div className="form-group">
          <Label>Hat Adı *</Label>
          <Input
            type="text"
            placeholder="Örn. Montaj Hattı 1"
            value={formData.name}
            onChange={(v) => setFormData({ ...formData, name: v })}
          />
        </div>
        <div className="form-group">
          <Label>Kod *</Label>
          <Input
            type="text"
            placeholder="Benzersiz kod"
            value={formData.code}
            onChange={(v) => setFormData({ ...formData, code: v })}
          />
        </div>
      </div>

      <div className="form-row">
        <div className="form-group">
          <Label>Ürün Hattı Tipi</Label>
          <Select
            options={productLineTypeOptions}
            selected={
              formData.productLineType ? [formData.productLineType] : []
            }
            onSelected={(v) =>
              setFormData({
                ...formData,
                productLineType: v?.[0]?.value || "production",
              })
            }
            unique
          />
        </div>
        <div className="form-group">
          <Label>Veri Giriş Tipi</Label>
          <Select
            options={dataEntryTypeOptions}
            selected={formData.dataEntryType ? [formData.dataEntryType] : []}
            onSelected={(v) =>
              setFormData({
                ...formData,
                dataEntryType: v?.[0]?.value || "manual",
              })
            }
            unique
          />
        </div>
      </div>

      <div className="form-group">
        <Label>Makineler (Sıra)</Label>
        <Button
          appearance="primary-subtle"
          onClick={addMachine}
          icon={<FiPlus />}
        >
          Makine Ekle
        </Button>
        {safeMachineItems.map((mi, idx) => (
          <div key={idx} className="machine-item-block mt-3">
            <div className="machine-item-header">
              <span className="order-btns">
                <IconButton
                  icon={<FiChevronUp size={14} />}
                  appearance="subtle"
                  shape="circle"
                  onClick={() => moveMachine(idx, -1)}
                  disabled={idx === 0}
                />
                <span className="order-num">{idx + 1}.</span>
                <IconButton
                  icon={<FiChevronDown size={14} />}
                  appearance="subtle"
                  shape="circle"
                  onClick={() => moveMachine(idx, 1)}
                  disabled={idx === formData.machineItems.length - 1}
                />
              </span>
              <Dropdown
                trigger={
                  <div className="dropdown-trigger">
                    {getMachineName(mi.machineId?._id || mi.machineId) ||
                      "Makine seçin"}
                  </div>
                }
                closeOnSelect
              >
                <DropdownContent>
                  {safeMachines
                    .filter((m) => {
                      const currentId = mi.machineId?._id || mi.machineId;
                      const usedElsewhere = safeMachineItems
                        .filter((_, i) => i !== idx)
                        .map((x) => x.machineId?._id || x.machineId);
                      return (
                        m &&
                        (m._id === currentId || !usedElsewhere.includes(m._id))
                      );
                    })
                    .map((m) => (
                      <DropdownItem
                        key={m?._id}
                        onClick={() => {
                          const upd = [...safeMachineItems];
                          upd[idx] = { ...upd[idx], machineId: m?._id };
                          setFormData({ ...formData, machineItems: upd });
                        }}
                        active={(mi.machineId?._id || mi.machineId) === m?._id}
                      >
                        {m?.name} ({m?.code})
                      </DropdownItem>
                    ))}
                </DropdownContent>
              </Dropdown>
              <IconButton
                icon={<FiTrash2 size={14} />}
                appearance="subtle"
                shape="circle"
                onClick={() => removeMachine(idx)}
              />
            </div>
            <div className="machine-item-continuity mt-2">
              <div className="form-row form-row--sm">
                <div className="form-group">
                  <div className="form-row form-row--sm">
                    <div className="form-group">
                      <Label>Ürün Girdisi</Label>
                      {idx === 0 ? (
                        <Select
                          options={[
                            { value: "", label: "—" },
                            ...safeInventoryItems.map((i) => ({
                              value: i._id,
                              label: `${i.name} (${i.code})`,
                            })),
                          ]}
                          selected={[mi.entryItemId || ""]}
                          onSelected={(v) => {
                            const upd = [...safeMachineItems];
                            upd[idx] = {
                              ...upd[idx],
                              entryItemId: v?.[0]?.value || undefined,
                            };
                            setFormData({ ...formData, machineItems: upd });
                          }}
                          unique
                          placeholder="Hammade / ilk giriş"
                        />
                      ) : (
                        <div
                          className="dropdown-trigger"
                          style={{ cursor: "default" }}
                        >
                          {derivedEntryId(idx)
                            ? getInvName(derivedEntryId(idx))
                            : "—"}
                        </div>
                      )}
                    </div>
                    <div className="form-group">
                      <Label>Adet</Label>
                      <Input
                        type="number"
                        min={0}
                        step={0.01}
                        value={mi.inputAmount ?? mi.inputPerOutput ?? 1}
                        onChange={(v) => {
                          const num = Number(v);
                          const upd = [...safeMachineItems];
                          const nextInput =
                            Number.isNaN(num) || num <= 0 ? 1 : num;
                          const currentOutput = upd[idx]?.outputAmount ?? 1;
                          upd[idx] = {
                            ...upd[idx],
                            inputAmount: nextInput,
                            outputAmount: currentOutput,
                            inputPerOutput:
                              currentOutput > 0
                                ? nextInput / currentOutput
                                : nextInput,
                          };
                          setFormData({ ...formData, machineItems: upd });
                        }}
                      />
                    </div>
                  </div>
                </div>
                <div className="form-group">
                  <div className="form-row form-row--sm">
                    <div className="form-group">
                      <Label>Ürün Çıktısı</Label>
                      <Select
                        options={[
                          { value: "", label: "—" },
                          ...safeInventoryItems.map((i) => ({
                            value: i._id,
                            label: `${i.name} (${i.code})`,
                          })),
                        ]}
                        selected={[mi.outputItemId || ""]}
                        onSelected={(v) => {
                          const upd = [...safeMachineItems];
                          upd[idx] = {
                            ...upd[idx],
                            outputItemId: v?.[0]?.value || undefined,
                          };
                          setFormData({ ...formData, machineItems: upd });
                        }}
                        unique
                        placeholder="Sonraki makineye giden"
                      />
                    </div>
                    <div className="form-group">
                      <Label>Adet</Label>
                      <Input
                        type="number"
                        min={0}
                        step={0.01}
                        value={mi.outputAmount ?? 1}
                        onChange={(v) => {
                          const num = Number(v);
                          const upd = [...safeMachineItems];
                          const nextOutput =
                            Number.isNaN(num) || num <= 0 ? 1 : num;
                          const currentInput =
                            upd[idx]?.inputAmount ??
                            upd[idx]?.inputPerOutput ??
                            1;
                          upd[idx] = {
                            ...upd[idx],
                            inputAmount: currentInput,
                            outputAmount: nextOutput,
                            inputPerOutput: currentInput / nextOutput,
                          };
                          setFormData({ ...formData, machineItems: upd });
                        }}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="form-group">
        <Label>Notlar</Label>
        <textarea
          className="form-control"
          placeholder="Notlar"
          value={formData.notes}
          onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
          rows={2}
        />
      </div>

      <div className="form-actions">
        <Button appearance="subtle" onClick={onReset}>
          İptal
        </Button>
        <Button appearance="primary-animated" onClick={onSubmit}>
          {editingLine ? "Güncelle" : "Oluştur"}
        </Button>
      </div>
    </div>
  );
}

export default ProductionLineForm;
